import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Link
} from "@mui/material";
import { useNav } from "../../context/NavProvider";
import { useClient } from "../../context/ClientProvider";
import ScriptLoader from "../account/ScriptLoader";
import CallMeDialogLogin from "./CallMeDialogLogin";
import CallMeDialog from "./CallMeDialog";
import { useAuth } from "../../context/AuthProvider";
import ActionButton from "../account/ActionButton";


function groupOpeningHours(hoursArr) {
  if (!Array.isArray(hoursArr) || hoursArr.length === 0) return [];

  const groups = [];
  let currentGroup = null;

  for (const entry of hoursArr) {
    const { day, closed, openingTime, closingTime } = entry;
    const key = closed ? 'closed' : `${openingTime}-${closingTime}`;

    if (
      !currentGroup ||
      currentGroup.key !== key
    ) {
      currentGroup = {
        days: [day],
        closed,
        ...(closed ? {} : { openingTime, closingTime }),
        key
      };
      groups.push(currentGroup);
    } else {
      currentGroup.days.push(day);
    }
  }

  return groups.map(({ key, days,...rest }) => {
    if (days.length === 1) {
      return {
        day: days[0],
        ...rest,
      } 
    } else {
      return {
        day: `${days[0]} - ${days[days.length - 1]}`,
        ...rest,
      }
    }
  });
}

function ContactUsPage() {
  const { setShowFooter } = useNav();
  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const { accessToken } = useAuth();
  const [groupedHours, setGroupedHours] = useState([]);
  const [openCallDialog, setOpenCallDialog] = useState(false);
  const contactUsDataPerClient = clientConfig?.layout?.unauthenticatedPages.contactUs;

  useEffect(() => {
    const daily = clientConfig?.layout?.callMe?.dailyAttributes ?? [];

    const openingHours = {
      Monday: {closed: true},
      Tuesday: {closed: true},
      Wednesday: {closed: true},
      Thursday: {closed: true},
      Friday: {closed: true},
      Saturday: {closed: true},
      Sunday: {closed: true},
    }

    for (const day of daily) {
        if (day.day && day.openingTime && day.closingTime) {
          openingHours[day.day] = {
            closed: false,
            openingTime: day.openingTime,
            closingTime: day.closingTime
        }
      }
    }
    const openingHoursArray = Object.entries(openingHours).map(([day, times]) => ({day, ...times}));
    const groupedOpeningHours = groupOpeningHours(openingHoursArray);
    setGroupedHours(groupedOpeningHours);
  }, [clientConfig, setGroupedHours]);


  useEffect(() => {
    setShowFooter(true);
  }, [setShowFooter]);

  const handleOpenCallback = () => {
    setOpenCallDialog(true);
  };

  const handleCloseCallback = () => {
    setOpenCallDialog(false);
  };

  return (
    <>
      <ScriptLoader />
      <Container sx={{ mt: 4, mb: 7 }}>
        <Box>
          <Typography variant='h1' fontWeight='bold' fontSize={32} sx={{ mb: 2, mt: 4 }}>
            Contact us
          </Typography>
          {clientConfig?.layout.callMe.active && (
            <Box sx={{ mb: 4 }}>
              <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ mb: 2 }}>
                Call Me
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Use our 'Call Me' function and we'll ring you back at a time that suits you.
              </Typography>
              <ActionButton
                variant='contained'
                buttonColor='secondary.main'
                buttonTextColor='primary.contrastText'
                borderColor='transparent'
                hoverColor='primary.contrastText'
                hoverBorderColor='secondary.main'
                hoverTextColor='secondary.main'
                buttonTitle='Call me'
                onClick={handleOpenCallback}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          <Box sx={{ mb: 4 }}>
            {!accessToken && clientConfig?.layout.callMe.active ? (
              <CallMeDialogLogin
                open={openCallDialog}
                onClose={handleCloseCallback}
              />
            ) : (
              <CallMeDialog
                open={openCallDialog}
                onClose={handleCloseCallback}
              />
            )}
          </Box>

          {contactUsDataPerClient?.phoneUs?.enabled && (
            <Box sx={{ mb: 4 }}>
              <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ mb: 2 }}>
                Phone us
              </Typography>
              <Typography sx={{ mb: 3 }}>
                Call the team directly on <Link href={`tel:${contactUsDataPerClient?.phoneUs?.phoneNumber}`}>{contactUsDataPerClient?.phoneUs?.phoneNumber}</Link>
              </Typography>
            </Box>
          )}

          {contactUsDataPerClient?.loanApproval?.enabled && (
            <Box sx={{ mb: 4 }}>
              <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ mb: 2 }}>
                Was your loan approved before December 2021?
              </Typography>
              <Typography sx={{ mb: 2 }}>
                If you have taken out a <b>personal loan</b> or a <b>point of sale loan</b> (through one of our approved partners) <b>before</b> 13th December 2021,
                please use the following contact details.
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Please note, if you are contacting us about additional borrowing on your existing loan, we do not offer this. However, you may be eligible to apply for another loan with us.
                We are unable to carry out phone applications but you can apply via our <Link href={`${contactUsDataPerClient?.loanApproval?.url}`}>website</Link> or one of our partners.
              </Typography>
              <Typography sx={{ mb: 3 }}>
                Call the team directly on <Link href={`tel:${contactUsDataPerClient?.phoneUs?.phoneNumber}`}>{contactUsDataPerClient?.phoneUs?.phoneNumber}.</Link>
              </Typography>
            </Box>
          )}

          {contactUsDataPerClient?.openingHours?.enabled && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ py: 1 }}>
            Opening hours
          </Typography>
          <Box sx={{ pb: 5 }}>
            <Typography aria-label="opening hours table">
              {groupedHours.map((row, item) => (
                <Box
                  key={item}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    maxWidth: 400,
                    mb: 1,
                  }}
                >
                  <Typography>{row.day}</Typography>
                  {row.closed ? (
                    <Typography>Closed</Typography>
                  ) : (
                    <Typography>
                      {row.openingTime} - {row.closingTime}
                    </Typography>
                  )}
                </Box>
              ))}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    maxWidth: 400,
                    mb: 1,
                  }}
                >
                  <Typography>Bank Holidays</Typography>
                  <Typography>Closed</Typography>
                </Box>
            </Typography>
          </Box>
            </>)}
          {contactUsDataPerClient?.writeOrSendEmail?.enabled && (
            <Box sx={{ mb: 4 }}>
              <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ mb: 2 }}>
                Write to us or send us an email
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Email the team (making sure to detail your full name and your account Reference Number) at:
              </Typography>
              <Link href={`mailto:${contactUsDataPerClient?.writeOrSendEmail.emailAddress}`}>
                {contactUsDataPerClient?.writeOrSendEmail.emailAddress}
              </Link>
              <Typography sx={{ mt: 2, mb: 2 }}>If you're looking to send details in the post, again please include your full name and your Reference Number. Our address is:</Typography>
              <Typography sx={{ mb: 1 }}>{contactUsDataPerClient?.writeOrSendEmail.address1}</Typography>
              <Typography sx={{ mb: 1 }}>{contactUsDataPerClient?.writeOrSendEmail?.address2}</Typography>
              <Typography sx={{ mb: 1 }}>{contactUsDataPerClient?.writeOrSendEmail?.address3}</Typography>
              <Typography sx={{ mb: 1 }}>{contactUsDataPerClient?.writeOrSendEmail?.postCode}</Typography>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
}

export default ContactUsPage;
